/**
 * Real-time state of a single deep email-research job.
 *
 * Subscribes to `users/{uid}/emailResearch/{jobId}` (read-only per rules) so the
 * chat can narrate the background pipeline's progress. The document is created by
 * the backend a moment after the tool returns, so an absent doc is treated as
 * "ainda não começou" (job stays null), not an error.
 */
import type { EmailResearchJobDoc } from '@agente/shared'
import { onSnapshot } from 'firebase/firestore'
import { useEffect, useState } from 'react'

import { useAuth } from '@/lib/auth'
import { emailResearchDoc } from '@/lib/firestore'

export interface UseEmailResearchJobResult {
  job: EmailResearchJobDoc | null
  loading: boolean
  error: string | null
}

export function useEmailResearchJob(jobId: string | null): UseEmailResearchJobResult {
  const { user } = useAuth()
  const uid = user?.uid ?? null

  const [job, setJob] = useState<EmailResearchJobDoc | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!uid || !jobId) {
      setJob(null)
      setLoading(false)
      return
    }
    setJob(null)
    setLoading(true)
    setError(null)

    const unsubscribe = onSnapshot(
      emailResearchDoc(uid, jobId),
      (snap) => {
        // Corrida entre a tool retornar e o pipeline criar o doc: doc ausente
        // não é erro — mantemos job null e seguimos ouvindo.
        setJob(snap.exists() ? { ...snap.data(), id: snap.id } : null)
        setLoading(false)
      },
      (err) => {
        setError(err.message)
        setLoading(false)
      },
    )
    return unsubscribe
  }, [uid, jobId])

  return { job, loading, error }
}
